"use client";

import { FetchButton } from "@/fetch-ui/FetchButton";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { CalendarDays, Upload, Users, type LucideIcon } from "lucide-react";
import { useRef, type ReactNode } from "react";

import { useEstimator } from "../../_context/EstimatorContext";
import { useAttendeeFile } from "../../_hooks/useAttendeeFile";
import { DATES_TRIGGER_ID, WHO_TRIGGER_ID } from "../../_logic/constants";
import { missingInputs } from "../../_logic/estimate";
import { FADE, POP_IN } from "../../_logic/motion";
import { ATTENDEE_FILE_ACCEPT } from "../../_logic/read-attendee-file";
import { canPriceFares } from "../../_logic/trip-dates";
import { knownTravelers } from "../../_logic/workspace";
import { AttendeeUploadHint } from "../attendees/AttendeeUploadHint";

/** One "flights aren't priced because…" strip: what's missing, and the action that fixes it. */
interface Notice {
  key: string;
  icon: LucideIcon;
  text: string;
  primary: { label: string; onClick: () => void };
  /** The quieter second way in, e.g. uploading a list instead of typing cities. */
  secondary?: ReactNode;
  /** What sits under the text, e.g. which files an upload takes. */
  hint?: ReactNode;
}

const openDates = () => document.getElementById(DATES_TRIGGER_ID)?.click();
const openWho = () => document.getElementById(WHO_TRIGGER_ID)?.click();

/**
 * Everything that leaves flights (or hotels) unpriced, said once above the comparison with the
 * fix beside it: no dates yet, dates that have passed or start too soon to book, no home cities
 * yet, and a headcount with only a few home cities known. Each strip has one filled action and,
 * where a list can replace typing, an upload beside it.
 * @returns The notices, or nothing when everything can be priced.
 * @throws Never.
 */
export const PricingNotices = () => {
  // Hooks
  const { workspace, estimate } = useEstimator();
  const { upload, reading } = useAttendeeFile();
  const reduceMotion = useReducedMotion();
  const fileInput = useRef<HTMLInputElement>(null);

  // Derived
  const missing = missingInputs(workspace);
  const datesPassed = workspace.dates !== null && !canPriceFares(workspace.dates);
  const placed = knownTravelers(workspace);
  const fewPlaced = workspace.origins.length > 0 && estimate.withoutHomeCity > 0;
  const uploadList = (
    <FetchButton
      variant="text"
      size="sm"
      color="neutral"
      type="button"
      loading={reading}
      leftIcon={<Upload aria-hidden className="size-3.5" />}
      onClick={() => fileInput.current?.click()}
    >
      Upload list
    </FetchButton>
  );

  const notices: Notice[] = [];
  if (missing.includes("dates")) {
    notices.push({
      key: "dates",
      icon: CalendarDays,
      text: "Add dates to price flights and hotels.",
      primary: { label: "Add dates", onClick: openDates },
    });
  } else if (datesPassed) {
    notices.push({
      key: "dates-passed",
      icon: CalendarDays,
      text:
        workspace.dates && workspace.dates.start < new Date().toISOString().slice(0, 10)
          ? "These dates have passed. Pick new dates to price flights."
          : "These dates are too soon to book. Pick later dates to price flights.",
      primary: { label: "Change dates", onClick: openDates },
    });
  }
  if (missing.includes("origins")) {
    notices.push({
      key: "origins",
      icon: Users,
      text: "Add home cities or upload an attendee list to price flights.",
      primary: { label: "Add home cities", onClick: openWho },
      secondary: uploadList,
      hint: <AttendeeUploadHint />,
    });
  } else if (fewPlaced) {
    notices.push({
      key: "few-placed",
      icon: Users,
      text: `Flights priced for ${placed.toLocaleString("en-US")} of ${estimate.headcount.toLocaleString("en-US")}. Add home cities or upload a list for the rest.`,
      primary: { label: "Add home cities", onClick: openWho },
      secondary: uploadList,
      hint: <AttendeeUploadHint />,
    });
  }

  // Render
  return (
    <>
      <AnimatePresence initial={false}>
        {notices.map((notice) => (
          <motion.div
            key={notice.key}
            role="status"
            {...(reduceMotion ? FADE : POP_IN)}
            className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl bg-gray-50 px-4 py-3"
          >
            {/* Icon + text wrap as one item, so on a narrow strip the actions drop below them. */}
            <div className="flex min-w-0 flex-1 basis-64 items-center gap-4">
              <notice.icon aria-hidden className="size-4 shrink-0 text-amber-600" />
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="text-sm font-medium text-gray-800">{notice.text}</span>
                {notice.hint}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-1">
              {notice.secondary}
              <FetchButton size="sm" type="button" onClick={notice.primary.onClick}>
                {notice.primary.label}
              </FetchButton>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
      <input
        ref={fileInput}
        type="file"
        accept={ATTENDEE_FILE_ACCEPT}
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void upload(file);
          event.target.value = "";
        }}
      />
    </>
  );
};
